import { Icon, IconButton, Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import getUserProfile from "js/core/services/userProfiles";
import { Thumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import moment from "moment";
import React, { Component } from "react";
import styled, { css } from "styled-components";

import { getCjkUiFontFamilyCss } from "common/fontConstants";
import { IWorkspaceResource, IWorkspaceResourceFolder, PaginatedSort } from "common/interfaces";
import PresentationLibraryPaneController, { PresentationLibraryFilter, PresentationLibraryPaneControllerState, PresentationLibraryPresentation } from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import { getPresentation } from "js/core/models/presentation";
import AvatarStack, { AvatarStackProfile } from "js/editor/PresentationLibrary/components/AvatarStack";
import { FolderLocationBreadcrumb } from "js/editor/PresentationLibrary/components/FolderLocationBreadcrumb";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { getPresentationContextMenuItems } from "js/editor/PresentationLibrary/PresentationContextMenu";
import { fetchPresentationCollaboratorProfiles } from "js/editor/PresentationLibrary/utils/fetchPresentationCollaboratorProfiles";
import { getLastAccessedLabel, LastActivityTooltipStyles } from "js/editor/PresentationLibrary/utils/getLastAccessedLabel";
import { app } from "js/namespaces";
import Avatar from "js/react/components/Avatar";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import LoadableAsync from "js/react/components/LoadableAsync";
import { ContextMenu, IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { mouseDragItems } from "js/react/views/SharedResources/utils/mouseDragHelper";
import { _ } from "js/vendor";
import ReadOnlyBadge from "./components/ReadOnlyBadge";

const sharedLibraryListLayout = css`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 80px minmax(50px, 3fr) minmax(60px, 2fr) minmax(60px, 1fr) minmax(40px, 2fr) minmax(50px, 1fr) minmax(50px, 1fr) 40px;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  column-gap: 20px;

  & > * {
    min-width: 0;
  }

  @media (max-width: 1200px) {
    column-gap: 16px;
  }
`;

const LibraryListRoot = styled.div`
  width: 100%;

`;

const LibraryListHeader = styled.div`
  ${sharedLibraryListLayout}
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  padding-right: 10px;
  padding-left: 10px;
  text-transform: uppercase;
`;

const LibraryListContainer = styled.div`
    height: 100%;

    .dark-mode & {
        border-top: 2px solid #222;
    }
`;

const LibraryListItem = styled.div <{ selected: boolean; readOnly: boolean }>`
  ${sharedLibraryListLayout}
  background: ${props => props.selected ? themeColors.lightBlue : "white"};
  border-bottom: 2px solid #f5f5f9;
  padding: 10px 10px;
  letter-spacing: 0px;
  ${({ readOnly = false }) => !readOnly && css<{ selected: boolean }>`
    &:hover {
        background: ${props => props.selected ? themeColors.lightBlue : themeColors.hoverBlue};
    }`}
  .thumbnail {
    width: 80px;
    height: 45px;
    img {
      height: 100%;
      outline: solid 1px #ddd;
    }
  }
  .title {
    font-weight: 600;
    font-family: "Source Sans Pro", ${getCjkUiFontFamilyCss()};
    ${!app.isMobileOrTablet && css`
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `}
  }
  .col {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .avatar {
    width: 25px;
    height: 25px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .dark-mode & {
    background: ${props => props.selected ? "#444" : "#333"};
    border-bottom: 2px solid #222;

    ${({ readOnly = false }) => !readOnly && css<{ selected: boolean }>`
    &:hover {
        background: ${props => props.selected ? "#444" : "#555"};
    }`}
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 45px;
`;

const LastAccessedLabelText = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 15px;
  min-width: 0;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 2px;
    align-items: stretch;

    .title {
      min-width: 0;
    }
  }
`;

const LibraryListItemSkeletonContainer = styled.div`
    height: 67px;

    .MuiSkeleton-root {
        height: 100%;
    }
`;

type PresentationLibraryListProps = PresentationLibraryPaneControllerState & {
    onSelectedPresentations?: (presentations: PresentationLibraryPresentation[]) => void;
    onDoubleClick?: (presentation: PresentationLibraryPresentation) => void;
    onStartDragItems?: (items: PresentationLibraryPresentation[]) => void;
    onDropDragItems?: (items: PresentationLibraryPresentation[]) => void;
    readOnly?: boolean;
    resourceFolders?: IWorkspaceResourceFolder[];
    onNavigateToFolder?: (filter: PresentationLibraryFilter) => void;
    showHeader?: boolean;
}

export const PresentationLibraryList = WorkspaceController.withState(PresentationLibraryPaneController.withState(
    class PresentationLibraryList extends Component<PresentationLibraryListProps, {
        selection: PresentationLibraryPresentation[];
        showContextMenu: boolean;
        contextMenuX: number;
        contextMenuY: number;
    }> {
        listRef: React.RefObject<HTMLDivElement>;
        startSelectionIndex: number;
        dragStarted: boolean;
        itemWasSelectedOnMouseDown: boolean;

        constructor(props: PresentationLibraryListProps) {
            super(props);

            this.state = {
                selection: [],
                showContextMenu: false,
                contextMenuX: 0,
                contextMenuY: 0
            };

            this.listRef = React.createRef();
            this.dragStarted = false;
            this.itemWasSelectedOnMouseDown = false;
        }

        getCurrentSelection = () => {
            const presentations = PresentationLibraryPaneController.legacyData;
            return this.state.selection.map(selectedPresentation =>
                presentations.find(presentation => presentation.id === selectedPresentation.id) ?? selectedPresentation
            );
        }

        isSelected = (selection: PresentationLibraryPresentation[], item: PresentationLibraryPresentation) => {
            return selection.some(selectedItem => selectedItem.id === item.id);
        }

        handleItemVisibilityChange = (visible: boolean, presentationId: string) => {
            const { data, fetching } = this.props;

            if (!visible || fetching) {
                return;
            }

            if (!PresentationLibraryPaneController.canLoadMore) {
                return;
            }

            const index = data.findIndex(reord => reord.document.id === presentationId);
            if (index === data.length - 1) {
                PresentationLibraryPaneController.loadMore();
            }
        }

        handleMouseDownOnItem = (item: PresentationLibraryPresentation, event: React.MouseEvent<HTMLDivElement>) => {
            const { readOnly } = this.props;

            const presentations = PresentationLibraryPaneController.legacyData;
            let selection = this.getCurrentSelection();

            if (readOnly) {
                return;
            }

            event.stopPropagation();
            this.itemWasSelectedOnMouseDown = this.isSelected(selection, item);

            if (event.metaKey || event.ctrlKey) {
                if (this.isSelected(selection, item)) {
                    selection = selection.filter(i => i.id !== item.id);
                } else {
                    selection = [...selection, item];
                }
            } else if (event.shiftKey) {
                const startIndex = this.startSelectionIndex || 0;
                const endIndex = presentations.indexOf(item);

                if (endIndex > startIndex) {
                    for (let i = startIndex + 1; i <= endIndex; i++) {
                        let item = presentations[i];
                        if (!this.isSelected(selection, item)) {
                            selection = [...selection, item];
                        }
                    }
                } else {
                    for (let i = startIndex; i >= endIndex; i--) {
                        let item = presentations[i];
                        if (!this.isSelected(selection, item)) {
                            selection = [...selection, item];
                        }
                    }
                }
            } else {
                if (!this.isSelected(selection, item)) {
                    selection = [item];
                    this.startSelectionIndex = presentations.indexOf(item);
                }
                this.dragItems(event, item, selection);
            }

            this.setState({ selection });
        }

        handleMouseUpOnItem = (item: PresentationLibraryPresentation, event: React.MouseEvent<HTMLDivElement>) => {
            const { onSelectedPresentations, readOnly } = this.props;
            const selection = this.getCurrentSelection();
            const didDrag = this.dragStarted;
            const itemWasSelectedOnMouseDown = this.itemWasSelectedOnMouseDown;
            const isDeselect = itemWasSelectedOnMouseDown && !didDrag && selection.length === 1 && this.isSelected(selection, item);

            if (!isDeselect && onSelectedPresentations) {
                onSelectedPresentations([item]);
            }

            if (readOnly) {
                return;
            }

            event.preventDefault();

            if (event.shiftKey || event.metaKey || event.ctrlKey) {
                return;
            }

            this.setState({ selection: isDeselect ? [] : [item] });
        }

        dragItems(event, mousedItem, selection) {
            this.dragStarted = false;

            mouseDragItems({
                event,
                container: this.listRef.current,
                mousedItem,
                selection,
                imageBorderColor: "#333",
                onDidStartDrag: () => {
                    this.dragStarted = true;
                },
                onStartDragItems: this.props.onStartDragItems,
                onDropDragItems: this.props.onDropDragItems,
            });
        }

        handleContextMenu = event => {
            const { readOnly } = this.props;
            if (readOnly) {
                return;
            }

            event.preventDefault();
            this.setState({ showContextMenu: true, contextMenuX: event.pageX, contextMenuY: event.pageY });
        }

        handleCloseContextMenu = () => {
            this.setState({ showContextMenu: false });
        }

        async loadPermission(selection) {
            if (selection.length === 1) {
                let item = selection[0];
                await item?.presentationModel?.getUserPermissions(true);
            } else {
                return;
            }
        }

        handleSort = (sort: PaginatedSort) => {
            PresentationLibraryPaneController.setSort([sort]);
        }

        render() {
            const { sort, readOnly, onDoubleClick, showHeader = !app.isConstrained, resourceFolders, onNavigateToFolder, fetching, search, data } = this.props;
            const { showContextMenu, contextMenuX, contextMenuY } = this.state;

            const selection = this.getCurrentSelection();
            const filter = PresentationLibraryPaneController.libraryFilter;
            const presentations = PresentationLibraryPaneController.legacyData;
            let currentSort = sort[0];
            if (search.query) {
                currentSort = null;
            }

            return (
                <LibraryListRoot className="library-list" ref={this.listRef}>
                    <LastActivityTooltipStyles />
                    {showHeader &&
                        <LibraryListHeader>
                            <div></div>
                            <ColumnHeader
                                label="Name"
                                field="document.name"
                                sort={currentSort}
                                disabled={!!search.query}
                                onSort={this.handleSort} />
                            <ColumnHeader
                                label="Edited"
                                field="resource.metadata.lastActivityByType.edited.createdAt"
                                sort={currentSort}
                                disabled={!!search.query}
                                onSort={this.handleSort} />
                            <ColumnHeader
                                label="Created"
                                field="document.createdAt"
                                sort={currentSort}
                                disabled={!!search.query}
                                onSort={this.handleSort} />
                            <ColumnHeaderDiv>
                                Folders
                            </ColumnHeaderDiv>
                            <ColumnHeaderDiv>
                                Shared
                            </ColumnHeaderDiv>
                            <ColumnHeader
                                label="Creator"
                                field="resource.owner.name"
                                sort={currentSort}
                                disabled={!!search.query}
                                onSort={this.handleSort} />
                            <div />
                        </LibraryListHeader>
                    }
                    <LibraryListContainer>
                        {presentations.map((presentation, idx) => (
                            <PresentationLibraryListItem
                                key={`${presentation.id}:${idx}`}
                                item={presentation}
                                readOnly={readOnly}
                                selected={this.isSelected(selection, presentation)}
                                allSelected={selection}
                                onMouseDown={event => this.handleMouseDownOnItem(presentation, event)}
                                onMouseUp={event => this.handleMouseUpOnItem(presentation, event)}
                                onDoubleClick={onDoubleClick}
                                onContextMenu={this.handleContextMenu}
                                filter={filter}
                                resourceFolders={resourceFolders}
                                resource={data.find(d => d.document.id === presentation.id)?.resource}
                                onNavigateToFolder={onNavigateToFolder}
                                onVisibleChange={this.handleItemVisibilityChange}
                                onMenuClick={item => {
                                    if (!this.isSelected(selection, item)) {
                                        this.setState({ selection: [] });
                                    }
                                }}
                            />
                        ))}
                        {fetching &&
                            <PresentationLibraryListItemSkeleton />
                        }
                    </LibraryListContainer>

                    {showContextMenu &&
                        <LoadableAsync
                            load={() => this.loadPermission(selection)}
                            renderChildren={() => (
                                <ContextMenu
                                    // @ts-ignore
                                    x={contextMenuX}
                                    y={contextMenuY}
                                    onClose={this.handleCloseContextMenu}
                                >
                                    {getPresentationContextMenuItems({ selectedPresentations: selection })}
                                </ContextMenu>
                            )}
                        />
                    }
                </LibraryListRoot>
            );
        }
    }
));

const ColumnHeaderDiv = styled.div`
  display: flex;
  align-items: center;

  font-family: "Source Sans Pro";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
  text-transform: capitalize;
  
  .MuiIcon-root {
    font-size: 14px;
    margin-left: 10px;
    color: #666;
  }
`;

class ColumnHeader extends Component<{
    label: string;
    field: string;
    sort?: PaginatedSort;
    onSort: (sort: PaginatedSort) => void;
    disabled?: boolean;
}> {
    handleClick = event => {
        const { field, sort, onSort, disabled } = this.props;
        if (disabled) {
            return;
        }
        onSort({ field, direction: sort.direction === "asc" ? "desc" : "asc" });
    }

    render() {
        const { label, field, sort } = this.props;

        return (
            <ColumnHeaderDiv onClick={this.handleClick}>
                {label}
                {sort?.field == field && sort?.direction === "asc" && <Icon>arrow_upward</Icon>}
                {sort?.field == field && sort?.direction === "desc" && <Icon>arrow_downward</Icon>}
            </ColumnHeaderDiv>
        );
    }
}

interface PresentationLibraryListItemProps {
    item: PresentationLibraryPresentation;
    readOnly: boolean;
    selected: boolean;
    allSelected: PresentationLibraryPresentation[];
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp: (event: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick: (presentation: PresentationLibraryPresentation) => void;
    filter: PresentationLibraryFilter;
    resourceFolders?: IWorkspaceResourceFolder[];
    resource?: IWorkspaceResource;
    onNavigateToFolder?: (filter: PresentationLibraryFilter) => void;
    onVisibleChange?: (visible: boolean, presentationId: string) => void;
    onMenuClick?: (item: PresentationLibraryPresentation) => void;
}

class PresentationLibraryListItem extends Component<PresentationLibraryListItemProps, {
    url: string;
    ownerName: string;
    avatarUrl: string;
    visible: boolean;
    avatarProfiles: AvatarStackProfile[];
}> {
    isMounted = false;
    ref: React.RefObject<HTMLDivElement>;
    observer: IntersectionObserver;

    constructor(props) {
        super(props);

        this.state = {
            url: "",
            ownerName: "",
            avatarUrl: "",
            visible: false,
            avatarProfiles: [],
        };

        this.ref = React.createRef();
    }

    componentDidMount() {
        this.isMounted = true;
        this.observer = new IntersectionObserver(entries => {
            const visible = entries[0].isIntersecting === true;
            this.props.onVisibleChange?.(visible, this.props.item.id);

            if (visible) {
                if (_.isEmpty(this.state.url)) {
                    this.fetchImages();
                }
                if (!this.state.visible) {
                    this.setState({ visible: true });
                }
            } else {
                if (this.state.visible) {
                    this.setState({ visible: false });
                }
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentDidUpdate(prevProps: PresentationLibraryListItemProps, prevState: { visible: boolean; avatarProfiles: AvatarStackProfile[] }) {
        if (!prevState.visible && this.state.visible && this.state.avatarProfiles.length === 0) {
            this.fetchCollaboratorProfiles();
        }
    }

    componentWillUnmount() {
        this.isMounted = false;
        this.observer.disconnect();
    }

    async fetchCollaboratorProfiles() {
        const { item } = this.props;
        try {
            const profiles = await fetchPresentationCollaboratorProfiles({
                workspaceId: item.workspaceId,
                resourceId: item.id,
                ownerUid: item.ownerUid,
            });
            if (this.ref.current) this.setState({ avatarProfiles: profiles });
        } catch {
            // ignore collaborator fetch errors
        }
    }

    async fetchImages() {
        const { item } = this.props;

        if (!this.isMounted) {
            return;
        }

        await Promise.all([
            (async () => {
                const url = await item.getThumbnailUrl();
                this.setState({ url });
            })(),
            (async () => {
                const { displayName, email, photoURL } = await getUserProfile(item.ownerUid);
                this.setState({
                    ownerName: displayName ?? email ?? "",
                    avatarUrl: photoURL ?? ""
                });
            })()
        ]);
    }

    handleSharePresentation = async selectedPresentation => {
        const presentation = await getPresentation(selectedPresentation.id, { permission: "write", autoSync: true });
        ShowDialog(ShareDialog, { presentation, onClose: () => presentation.disconnect() });
    }

    render() {
        const { item, readOnly, onMouseDown, onMouseUp, onContextMenu, selected, onDoubleClick, resourceFolders, onNavigateToFolder, allSelected, resource, onMenuClick } = this.props;
        const { url, ownerName, avatarUrl, visible, avatarProfiles } = this.state;

        const lastAccessedLabel = getLastAccessedLabel(item);

        return (
            <LibraryListItem
                className="library-list-item"
                data-item-id={item.id}
                selected={selected}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onDoubleClick={() => onDoubleClick && onDoubleClick(item)}
                onContextMenu={onContextMenu}
                ref={this.ref}
                readOnly={readOnly}
            >
                {!visible && <Placeholder />}
                {visible && <>
                    <Thumbnail readOnly={readOnly} url={item.dirty ? null : url} showSpinner={true} />

                    <TitleRow>
                        <TruncatedTooltip title={item.name}>
                            <div className="title">{item.name}</div>
                        </TruncatedTooltip>
                        {(!item.permissions.owner && !item.permissions.write) && <ReadOnlyBadge />}
                    </TitleRow>

                    {
                        !app.isConstrained &&
                        <>
                            <div className="col">
                                <Tooltip
                                    title={`${lastAccessedLabel?.actionLabel ?? ""} by ${lastAccessedLabel?.userName ?? ""}`}
                                    classes={{ tooltip: "last-activity-tooltip" }}
                                    open={lastAccessedLabel?.userName === "you" ? false : undefined}
                                >
                                    <LastAccessedLabelText>
                                        <span>{`${lastAccessedLabel?.label ?? "—"}`}</span>
                                    </LastAccessedLabelText>
                                </Tooltip>
                            </div>
                            <div className="col">{moment(item.createdAt).format("MMM D, YYYY")}</div>
                            <div className="col" style={{ minWidth: 0, overflow: "hidden" }}>
                                {resourceFolders && resource && onNavigateToFolder && (
                                    <FolderLocationBreadcrumb
                                        resource={resource}
                                        resourceFolders={resourceFolders}
                                        onNavigateToFolder={onNavigateToFolder}
                                    />
                                )}
                            </div>
                            <div className="col">
                                <AvatarStack profiles={avatarProfiles} />
                            </div>

                            <div className="col">
                                <Avatar url={avatarUrl} />
                                <TruncatedTooltip title={ownerName}>
                                    <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {ownerName}
                                    </div>
                                </TruncatedTooltip>
                            </div>
                            {
                                !readOnly &&
                                <div
                                    onMouseDown={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onMouseUp={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDoubleClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        if (allSelected.length > 0 && !allSelected.some(p => p.id === item.id)) {
                                            onMenuClick?.(item);
                                        }
                                    }}
                                >
                                    <IconMenu>
                                        {getPresentationContextMenuItems({
                                            selectedPresentations: allSelected.length > 0 && allSelected.some(p => p.id === item.id)
                                                ? allSelected
                                                : [item],
                                        })}
                                    </IconMenu>
                                </div>
                            }
                        </>
                    }
                    {
                        !window.isZoomApp &&
                        app.isMobileOrTablet &&
                        !readOnly &&
                        item.permissions.write &&
                        <>
                            <IconButton
                                className="text-blue"
                                onMouseDown={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onMouseUp={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onDoubleClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    this.handleSharePresentation(item);
                                }}
                            >
                                <Icon>share</Icon>
                            </IconButton>
                        </>
                    }
                </>}
            </LibraryListItem>
        );
    }
}

function PresentationLibraryListItemSkeleton() {
    return (
        <LibraryListItemSkeletonContainer>
            <Skeleton />
        </LibraryListItemSkeletonContainer>
    );
}
